import { Container } from '@tencent/alloy-renderer';
import { BuildTreeAsync } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/build-tree-async';
import { buildTreeSync } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/build-tree-sync';
import { IWrapperComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/types';
import { SlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide';

const ASYNC_BUILD_RETRY_MAX = 3;

export class SlideElements {
    public slideComponent: SlideComponent;
    public hasBuild;
    private asyncBuilder: BuildTreeAsync | null;
    private asyncBuild: boolean;
    private asyncRetryTimes: number;
    private taskGroupKey: string;

    public constructor(slideComponent: SlideComponent, asyncBuild = false) {
        this.slideComponent = slideComponent;
        this.hasBuild = false;
        this.asyncBuild = asyncBuild;
        this.asyncRetryTimes = 0;
    }

    public dispose(): void {
        // 不影响灰度发布先注释掉，跟着下个版本充分验证后再发
        if (this.asyncBuilder) {
            this.asyncBuilder.cancelBuild();
            this.asyncBuilder = null;
        }
    }

    public updateComponent(): void {
        if (this.hasBuild) {
            // 已经创建过的走同步创建树
            this.syncBuildTree();
            return;
        }
        if (this.asyncBuilder) {
            // 异步创建还未完成，要先取消掉创建任务
            this.asyncBuilder.cancelBuild();
            this.asyncBuilder = null;
            if (this.asyncRetryTimes < ASYNC_BUILD_RETRY_MAX) {
                this.asyncBuildTree();
            } else {
                // 然后再同步创建树
                this.syncBuildTree();
            }
        } else {
            // 现在策略为左侧缩略图第一次异步，其他场景同步创建
            if (this.asyncBuild) {
                this.asyncBuildTree();
            } else {
                this.syncBuildTree();
            }
        }
    }

    /**
     * 切换创建树的模式
     * @param sync
     */
    public switchBuildMode(sync: boolean): void {
        if (this.hasBuild) return;
        if (sync) {
            if (this.asyncBuilder) {
                // 异步创建还未完成，要先取消掉创建任务
                this.asyncBuilder.cancelBuild();
                this.asyncBuilder = null;
            }
            // 然后再同步创建树
            this.syncBuildTree();
        } else {
            this.asyncBuildTree();
        }
    }

    public syncBuildTree(): void {
        if (!this.slideComponent.elementsLayer) {
            this.slideComponent.elementsLayer = new Container();
            this.slideComponent.addChild(this.slideComponent.elementsLayer);
        }
        buildTreeSync({
            canvasLayer: this.slideComponent.elementsLayer,
            canvasArr: this.slideComponent.elementsLayer.children as IWrapperComponent[],
            modelsArr: this.slideComponent.options.slideModel.children,
            componentFactoryOptions: {
                isMasterEdit: this.slideComponent.options.isMasterEdit,
                model: null,
                getPageScale: this.slideComponent.options.getPageScale,
                getApplication: this.slideComponent.options.getApplication,
                region: this.slideComponent.options.region,
                isCopyToImage: this.slideComponent.options.isCopyToImage,
                fromScale: this.slideComponent.options.fromScale,
                logicService: this.slideComponent.options.logicService,
                handleSlideClick: this.slideComponent.options.handleSlideClick,
                getPlayerReduxStore: this.slideComponent.options.getPlayerReduxStore,
                getFilterDisplayOptions: this.slideComponent.getFilterDisplayOptions,
                mobileEditMode: this.slideComponent.options.mobileEditMode,
                slideModel: this.slideComponent.options.slideModel,
                infrastructureService: this.slideComponent.options.infrastructureService,
                uiDataService: this.slideComponent.getUiDataServiceDelegate(),
                selectionService: this.slideComponent.getSelectionServiceDelegate(),
                mediaManager: this.slideComponent.getMediaManagerDelegate(),
                animManager: this.slideComponent.getAnimManagerDelegate(),
                exportImageManager: this.slideComponent.getExportImageManager(),
                rightSideBarManager: this.slideComponent.getRightSideBarManager(),
                attachmentManager: this.slideComponent.getAttachmentManager(),
                editingStateManager: this.slideComponent.getEditingStateManager(),
                addonManager: this.slideComponent.getAddonManager(),
                editorAnimPreviewManager: this.slideComponent.getEditorAnimPreviewManager(),
                formatBrushManager: this.slideComponent.getFormatBrushManager(),
                refreshBackground: this.slideComponent.refreshBackground,
                previewService: this.slideComponent.options.previewService,
                textEditorStateService: this.slideComponent.options.textEditorStateService,
                historyManager: this.slideComponent.options.historyManager,
                copyPasteImageService: this.slideComponent.options.copyPasteImageService,
                masterLayoutEditService: this.slideComponent.options.masterLayoutEditService,
                fontManager: this.slideComponent.options.fontManager,
                linkManager: this.slideComponent.options.linkManager,
                accessibilityManager: this.slideComponent.options.accessibilityManager,
                undoRedoService: this.slideComponent.options.undoRedoService,
                textSelectionUndoRedo: this.slideComponent.options.textSelectionUndoRedo,
                editAreaCanvasSizeManager: this.slideComponent.options?.editAreaCanvasSizeManager,
                gifService: this.slideComponent.options?.gifService,
            },
        });
        this.hasBuild = true;
        this.slideComponent.slideElementsHasBuilt = this.checkSlideElementsHasBuilt();
    }

    // 第一次构建渲染树需要拆分任务减少阻塞
    public asyncBuildTree(): void {
        this.asyncRetryTimes += 1;
        this.taskGroupKey = this.generateTaskGroupKey();
        const elementsLayer = new Container();
        this.asyncBuilder = new BuildTreeAsync(
            this.slideComponent,
            elementsLayer,
            this.taskGroupKey,
            {
                selectionService: this.slideComponent.getSelectionServiceDelegate(),
            },
        );
        const currentTaskGroupKey = this.taskGroupKey;
        this.asyncBuilder.asyncBuildTree().then(() => {
            if (!this.hasBuild && this.taskGroupKey === currentTaskGroupKey) {
                if (this.slideComponent.isDispose) {
                    elementsLayer.dispose();
                } else {
                    // 异步构建结束，打开autoRender
                    const app = this.slideComponent.options.getApplication?.() || this.slideComponent.app;
                    if (app) {
                        app.autoRender = true;
                    }

                    this.slideComponent.elementsLayer = elementsLayer;
                    this.slideComponent.addChild(elementsLayer);
                    this.hasBuild = true;
                    this.asyncBuilder = null;
                    this.slideComponent.slideElementsHasBuilt = this.checkSlideElementsHasBuilt();
                }
            } else {
                elementsLayer.dispose();
            }
        });
    }

    private generateTaskGroupKey(): string {
        return `build-task-group-${this.slideComponent.options.region}-${this.asyncRetryTimes}-${this.slideComponent.options.slideModel.key}`;
    }

    private checkSlideElementsHasBuilt(): boolean {
        const canvasArrLength = this.slideComponent.elementsLayer.children.length;
        // @ts-ignore
        const sdkDataChildrenLength = this.slideComponent.options.slideModel.sdkData?.shapeList.length;
        return canvasArrLength === sdkDataChildrenLength;
    }
}
